import { useCallback, useEffect, useRef, useState } from "react";

import { useAppSelector } from "app/hooks";
import {
  RECORDING_STATE_STOPPED,
  TAB_SYNC_ACTIONS
} from "features/medical-scribe/medical-scribe-constants";
import { selectMedicalScribeRecordingState } from "features/medical-scribe/medical-scribe.slice";
import useSyncBetweenTabs, {
  type SyncMessage,
  dispatchTabSyncMessage
} from "hooks/use-sync-between-tabs.hook";

/** How often to re-confirm that the tab believed to be recording is still there. */
export const RECORDING_STATUS_RECHECK_MS = 10_000;
/**
 * How long the recording tab gets to answer a re-check. Its reply reads the session out
 * of IndexedDB first, so this is generous; a late reply simply re-arms the flag.
 */
export const RECORDING_STATUS_REPLY_GRACE_MS = 3_000;

/**
 * Whether a Scribe recording is live in this browser — in this tab or any other.
 *
 * The softphone runs in one leader tab per browser, which is often not the tab that is
 * recording, so Redux alone is blind to the case that matters (ENG-2001). The recording
 * tab already announces start/resume/pause/stop over the tab-sync channel and answers
 * REQUEST_RECORDING_STATUS while it owns a live session; this mirrors those into a flag.
 *
 * A paused recording counts as active: the clinician is mid-encounter and Resume will
 * reopen the microphone that a ringing call would otherwise take.
 *
 * A tab that crashes or is force-closed never broadcasts STOP, so while the flag says
 * another tab is recording it is re-confirmed on a cadence and lapses if nobody answers.
 * Otherwise the phone would decline every call until the leader tab was reloaded.
 */
export function useScribeRecordingActive(): boolean {
  const localState = useAppSelector(selectMedicalScribeRecordingState);
  const [activeOnAnotherTab, setActiveOnAnotherTab] = useState(false);
  const processedMessageIds = useRef(new Set<string>());
  const lapseTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const onMessage = useCallback((message: SyncMessage) => {
    switch (message.action) {
      case TAB_SYNC_ACTIONS.START_SCRIBE_RECORDING_ON_TAB:
      case TAB_SYNC_ACTIONS.RESUME_SCRIBE_RECORDING_ON_TAB:
      case TAB_SYNC_ACTIONS.PAUSE_SCRIBE_RECORDING_ON_TAB:
      case TAB_SYNC_ACTIONS.RECORDING_STATUS_RESPONSE:
        clearTimeout(lapseTimerRef.current);
        setActiveOnAnotherTab(true);
        break;
      case TAB_SYNC_ACTIONS.STOP_SCRIBE_RECORDING_ON_TAB:
        setActiveOnAnotherTab(false);
        break;
      default:
        break;
    }
  }, []);
  useSyncBetweenTabs(processedMessageIds, onMessage);

  // A tab that opens (or becomes leader) mid-recording missed the START broadcast.
  useEffect(() => {
    dispatchTabSyncMessage({
      action: TAB_SYNC_ACTIONS.REQUEST_RECORDING_STATUS
    });
  }, []);

  useEffect(() => {
    if (!activeOnAnotherTab) return undefined;
    const recheck = setInterval(() => {
      dispatchTabSyncMessage({
        action: TAB_SYNC_ACTIONS.REQUEST_RECORDING_STATUS
      });
      clearTimeout(lapseTimerRef.current);
      lapseTimerRef.current = setTimeout(
        () => setActiveOnAnotherTab(false),
        RECORDING_STATUS_REPLY_GRACE_MS
      );
    }, RECORDING_STATUS_RECHECK_MS);
    return () => {
      clearInterval(recheck);
      clearTimeout(lapseTimerRef.current);
    };
  }, [activeOnAnotherTab]);

  return localState !== RECORDING_STATE_STOPPED || activeOnAnotherTab;
}
